// client/src/modules/fieldService/nav.ts
import type { NavItem } from '../types';
import { isOfficeContractReader } from '../../config/contractAccess';

/**
 * Field Service nav — mirrors how the team works tickets in AppSheet.
 *
 * Primary items are the day-to-day surfaces (dashboard, tickets,
 * generators, calendar, recurring programs). Manager workflows live under "Tools".
 * Spec: docs/superpowers/specs/2026-07-07-field-service-overhaul-phase1-design.md §3
 */
export const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/field-service/dashboard' },
  { label: 'Tickets', path: '/field-service' },
  { label: 'Generators', path: '/field-service/generators' },
  { label: 'Calendar', path: '/field-service/schedule' },
  // Recurring + Contracts both mirror a `requireManagerOrAdmin` route guard. NavItem's
  // own docstring is the reason: "Mirror the destination route's requiredRole here so
  // the nav never advertises a page the user is bounced out of." Contracts was gated at
  // the route and NOT in the nav, so every technician saw a link to an Access Denied
  // screen; fixed here alongside Recurring (B3-RECURRING, 2026-09-15).
  { label: 'Recurring', path: '/field-service/schedules', requireManagerOrAdmin: true },
  // FSR Review is a daily office step, not a manager tool — promoted out of the Tools
  // dropdown to sit right after Recurring (team process review, 2026-09-22).
  { label: 'FSR Review', path: '/field-service/reports' },
  {
    label: 'Tools',
    path: '/field-service/recurring-review',
    children: [
      // Managers look for Recurring under Tools too (ISS-632); same route and same gate as the
      // top-level item, so technicians are never shown a link to an Access Denied screen.
      { label: 'Recurring', path: '/field-service/schedules', requireManagerOrAdmin: true },
      { label: 'Recurring Review', path: '/field-service/recurring-review' },
      { label: 'Follow-ups to Review', path: '/field-service/follow-ups' },
      { label: 'Pin Suggestions', path: '/field-service/pin-suggestions' },
      { label: 'Duplicate Generators', path: '/field-service/duplicate-generators', requireManagerOrAdmin: true },
      { label: 'Fluid Samples', path: '/field-service/samples' },
      { label: 'Sample Intake', path: '/field-service/samples/intake' },
      { label: 'Needs Attention', path: '/field-service/needs-attention' },
      // Visible to every P11 user: the page is read-only for non-managers, so a dispatcher can
      // see who a ticket defaults to. Writes are manager-gated on the server.
      { label: 'Ticket Owners', path: '/field-service/ticket-owners' },
      // Office contract readers (PR 5), case-insensitive like the server: mirrors the route's allowRole.
      { label: 'Contracts', path: '/field-service/contracts', allowRole: isOfficeContractReader },
      { label: 'Service Writing', path: '/field-service/service-writing' },
      // Read-only: waiting-to-be-billed tickets and recent invoices. Billing (P4) owns the actions.
      { label: 'Billing Status', path: '/field-service/billing-status' },
    ],
  },
];
